import { Controller } from "@hotwired/stimulus"
import { openChrome, closeChrome } from "../helpers/overlay_chrome"

// The soft rating ask (budgets/_review_prompt).
//
// Shows once a day through the trial, from the first expense on, and never on
// top of a celebration — if one is still on screen it waits for that card to
// close, so the goal card's own moment is never stepped on.
//
// The ask waits fifteen seconds so the dashboard is theirs first. Leaving
// before the wait is up cancels it outright — the timer is cleared on
// disconnect, so nothing is posted and nothing is counted as shown.
//
// Apple's sheet opens on "Rate now" and nowhere else. That tap turns the button
// green and swaps the copy for a thank-you before the sheet appears, then the
// server stamps review_asked_at through the bridge's own record URL, which
// retires this card for good.
export default class extends Controller {
  static targets = ["panel", "rate", "later", "sub", "review", "mascot"]
  static values = {
    shownUrl: String,
    dismissUrl: String,
    thanks: String,
    thanksMascot: String
  }

  // Raised once a day. The value stored is the local date it was raised on,
  // so a web view the shell keeps alive overnight asks again tomorrow.
  //
  // A Turbo snapshot taken while the card was on screen carries the whole card
  // back into the DOM, and this controller reconnects on it and would raise it
  // again. Marked on show() rather than on an answer, because show() is what
  // posts the showing: from that moment the server holds the card back until
  // tomorrow, so anything a restored snapshot raises today is a card the
  // server has already counted, and its second post would count the day
  // twice. Walking away without answering has to close it for the day too,
  // not just tapping one of the buttons.
  //
  // Mirrors bridge/app_review_controller's SESSION_KEY.
  static SESSION_KEY = "floosyo:review-prompt-seen"

  // How long the dashboard is left alone before the ask is raised.
  static SHOW_DELAY = 15000

  // Cards this render raises by itself on a short delay, so not open yet when
  // this connects: the streak +1 card (until it stamps itself raised) and the
  // first-import bank welcome.
  static PENDING_CARD_SELECTOR = ".streak-up:not([data-streak-up-raised]), .bank-welcome-deck"

  // Stamped on the card itself when it is raised, which means a Turbo snapshot
  // carries it. sessionStorage cannot be relied on here: the native shell runs
  // a separate web view per tab, and each one has its own sessionStorage, so a
  // card raised on the dashboard is unseen as far as a restore in another web
  // view is concerned. The attribute travels with the DOM it belongs to.
  static RAISED_ATTRIBUTE = "data-review-prompt-raised"

  // Two ways in, both on the same fifteen-second timer.
  //
  //   cold start — nothing on screen, so the countdown starts right away
  //   arc finish — a celebration owns the screen, so the countdown starts when
  //                that card closes ("See my dashboard" on the last one)
  connect() {
    // What the card looked like before anyone touched it, so a snapshot can be
    // handed back clean.
    this._pristineLabel = this.hasRateTarget ? this.rateTarget.textContent : null
    this._pristineMascot = this.hasMascotTarget ? this.mascotTarget.getAttribute("src") : null

    // Turbo caches the DOM as it stands, mid-answer included: the green
    // thank-you button, the cheering mascot, the stars. Restore it to the ask
    // it was before the snapshot is taken, so a card that does come back is
    // never a half-answered one.
    this._resetForCache = () => this._reset()
    document.addEventListener("turbo:before-cache", this._resetForCache)

    // Already raised in this DOM. A restored snapshot re-runs connect() on a
    // card the server has already counted for today, and re-raising it would
    // count the day twice.
    if (this.element.hasAttribute(this.constructor.RAISED_ATTRIBUTE)) return

    if (this._seen) return

    // Leaving the dashboard cancels the wait. Hotwire Native keeps this page
    // alive under a modal or a pushed screen, so disconnect never runs there
    // and the card would open unseen behind it, taking the tab bar with it.
    // The next dashboard render starts the wait again.
    this._onLeave = () => this._cancelWait()
    document.addEventListener("turbo:before-visit", this._onLeave)

    // The rating comes last: after a card that is open now, and after one
    // this render is about to open (the streak +1 card waits SHOW_DELAY).
    if (!this._celebrationOpen && !this._cardPending) {
      this._showAfterDelay()
      return
    }

    this._onCelebrationClosed = () => this._showAfterDelay()
    document.addEventListener("celebration:closed", this._onCelebrationClosed, { once: true })
  }

  get _cardPending() {
    return document.querySelector(this.constructor.PENDING_CARD_SELECTOR) !== null
  }

  _cancelWait() {
    if (this._showTimer) clearTimeout(this._showTimer)
    this._showTimer = null
    if (this._onCelebrationClosed) {
      document.removeEventListener("celebration:closed", this._onCelebrationClosed)
    }
  }

  get _seen() {
    try {
      return sessionStorage.getItem(this.constructor.SESSION_KEY) === this._today
    } catch (_) {
      return false
    }
  }

  _markSeen() {
    try {
      sessionStorage.setItem(this.constructor.SESSION_KEY, this._today)
    } catch (_) { }
  }

  // The device's local date. The server decides on the user's own timezone
  // setting; this only has to tell today from yesterday on one device.
  get _today() {
    return new Date().toDateString()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this._resetForCache)
    if (this._onLeave) document.removeEventListener("turbo:before-visit", this._onLeave)
    this._cancelWait()
    this._unlockScroll()
  }

  get _celebrationOpen() {
    return document.querySelector(
      ".welcome-cel:not([hidden]), .streak-cel:not([hidden]), .setup-prog:not([hidden]), .streak-up:not([hidden]), .due-deck:not([hidden]), .bank-review-deck:not([hidden])"
    ) !== null
  }

  // Fifteen seconds, then the ask. The wait also settles the bridges this card
  // depends on: it is rendered above the nav-bar and bottom-nav bridge elements
  // in budgets/index, so Stimulus has not connected those controllers yet at
  // the moment this one connects, and showing synchronously would find neither
  // bridge and leave the native chrome undimmed behind the scrim.
  _showAfterDelay() {
    this._showTimer = setTimeout(() => this._showWhenClear(), this.constructor.SHOW_DELAY)
  }

  // The Due today deck can be opened from its dashboard card at any point in
  // the wait, so the clear screen is checked again when the wait is up.
  _showWhenClear() {
    if (!this._celebrationOpen) {
      this.show()
      return
    }

    this._onCelebrationClosed = () => this._showAfterDelay()
    document.addEventListener("celebration:closed", this._onCelebrationClosed, { once: true })
  }

  show() {
    this._markSeen()
    this.element.setAttribute(this.constructor.RAISED_ATTRIBUTE, "1")
    this.panelTarget.hidden = false
    this._lockScroll()
    this._applyChrome()
    // Same double-rAF re-assert the celebration popups use (see
    // welcome_celebration#connect). On a cold launch the first hide() can reach
    // the native side before the view controller is on screen: NavBarComponent
    // parks an early dim as pendingDimMessage and replays it, but the bottom-nav
    // component has no such replay, so its hide is simply dropped — the top ends
    // up dimmed with the tab bar still sitting over the scrim. The budget
    // carousel also calls bridge.show() for its first slide during load, which
    // would clobber an immediate hide. Re-asserting after both settle wins.
    requestAnimationFrame(() => requestAnimationFrame(() => this._applyChrome()))
    this._post(this.shownUrlValue)
  }

  _applyChrome() {
    openChrome(this, this.element)
  }

  // "Rate now": thank them first, then hand over to Apple. The card stays put
  // behind the sheet — dismissing it early would look like the tap failed.
  //
  // The mascot changes to its cheering pose and the button throws a handful of
  // stars, so the yes is answered on screen before Apple's sheet takes over.
  rate() {
    this._markSeen()
    this.rateTarget.classList.add("is-thanks")
    this.rateTarget.textContent = this.thanksValue
    this.rateTarget.disabled = true
    // "Maybe later" stays live. Apple's sheet can be cancelled, and it can be
    // slow to appear; whatever happens over there, the card must never be a
    // room with the door shut behind them.

    this._swapMascot()
    this._burstStars()

    setTimeout(() => {
      this._reviewBridge?.request()
      setTimeout(() => this._close(), 1500)
    }, 700)
  }

  _swapMascot() {
    if (!this.hasMascotTarget || !this.thanksMascotValue) return
    this.mascotTarget.src = this.thanksMascotValue
  }

  // Five stars thrown off the button, each on its own arc, removed once they
  // land so nothing is left behind the sheet.
  _burstStars() {
    const STARS = 5

    for (let index = 0; index < STARS; index++) {
      const star = document.createElement("span")
      star.className = "rate-ask-star"
      star.setAttribute("aria-hidden", "true")
      star.textContent = "✦"
      star.style.setProperty("--star-x", `${(index - (STARS - 1) / 2) * 34}px`)
      star.style.setProperty("--star-delay", `${index * 55}ms`)
      star.addEventListener("animationend", () => star.remove(), { once: true })
      this.rateTarget.appendChild(star)
    }
  }

  dismiss() {
    this._markSeen()
    this._post(this.dismissUrlValue)
    this._close()
  }

  _close() {
    this.panelTarget.hidden = true
    this._unlockScroll()
    closeChrome(this)
  }

  // Back to the ask as rendered: closed, blue, its own copy, its own pose, no
  // stars left over.
  _reset() {
    this.panelTarget.hidden = true
    this._unlockScroll()

    if (this.hasRateTarget) {
      this.rateTarget.classList.remove("is-thanks")
      this.rateTarget.disabled = false
      if (this._pristineLabel !== null) this.rateTarget.textContent = this._pristineLabel
      this.rateTarget.querySelectorAll(".rate-ask-star").forEach((star) => star.remove())
    }

    if (this.hasMascotTarget && this._pristineMascot) {
      this.mascotTarget.setAttribute("src", this._pristineMascot)
    }
  }

  get _reviewBridge() {
    if (!this.hasReviewTarget) return null
    return this.application.getControllerForElementAndIdentifier(
      this.reviewTarget, "bridge--app-review"
    )
  }

  _post(url) {
    if (!url) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", "Accept": "text/plain" },
      keepalive: true
    })
  }

  _lockScroll() {
    this._prevOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = "hidden"
  }

  // Only the lock this card took is released. `overflow` on <html> is shared —
  // welcome_celebration, month_recap and setup_progress all lock the same
  // property — and this runs on turbo:before-cache for every connected card,
  // including ones that never opened. Clearing it unconditionally handed back
  // a lock somebody else was still holding: navigate away with the month-recap
  // modal up, and the snapshot cached the page scrollable behind it.
  _unlockScroll() {
    if (this._prevOverflow === undefined) return

    document.documentElement.style.overflow = this._prevOverflow
    this._prevOverflow = undefined
  }
}
